import { SelectProps } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { FieldScope } from '@/proto/field';
import { TagFilter, TagFilterEditor, TagFilterEditorProps, TagFilterProps, TagFilterValue } from '../tag-filter';
import { createModelDropdownRender } from './marketing-name-alert';
import { useDeviceModelOptions } from './use-device-model-options';
import { modelFilterOption, useMarketingNameSearch } from './use-marketing-name-search';

export type ModelTagFilterProps = TagFilterProps;

export type ModelTagFilterEditorProps = TagFilterEditorProps;

interface ModelInjectedState {
  options: ReturnType<typeof useDeviceModelOptions>['options'];
  mergedValueSelectProps: SelectProps;
}

/** 默认的「包含」操作符（写入 values 用） */
const DEFAULT_OPERATOR = 'MATCH';

/**
 * 把 model 追加到 TagFilterValue 中现有 condition（没有则新建），返回新的 value。
 * 若 model 已存在则返回原 value。
 */
function addModelToTagValue(value: TagFilterValue | undefined, model: string): TagFilterValue {
  const conditions = value?.conditions ? [...value.conditions] : [];
  if (conditions.length === 0) {
    return { conditions: [{ operator: DEFAULT_OPERATOR, values: [model] }] };
  }
  const first = { ...conditions[0] };
  const list = Array.isArray(first.values) ? first.values : [];
  if (list.includes(model)) {
    return value!;
  }
  first.values = [...list, model];
  conditions[0] = first;
  return { conditions };
}

/**
 * 机型筛选共用状态组装
 *
 * - 懒加载：下拉首次展开时才请求型号 + 营销名称
 * - 搜索注入：filterOption 只匹配原始型号；本地无匹配时降级营销名称搜索
 * - 切换：handleSwitchToModel 把命中的 model 写入 value 并关闭下拉
 */
function useModelInjectedState(params: {
  fieldKey?: string;
  filterName?: string;
  fieldScope?: FieldScope | null;
  valueSelectProps?: SelectProps;
  value?: TagFilterValue;
  onChange?: (value: TagFilterValue) => void;
}): ModelInjectedState & { handleSwitchToModel: (model: string) => void } {
  const {
    fieldKey,
    filterName,
    fieldScope,
    valueSelectProps,
    value,
    onChange,
  } = params;

  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);

  const { options, loading } = useDeviceModelOptions({
    fieldKey: fieldKey || filterName,
    fieldScope,
    enabled,
  });

  const {
    marketingHit,
    searching,
    handleSearch,
    switchToModel,
    inputValue,
  } = useMarketingNameSearch({ options });

  // 点击型号项：把 model 写入 value，清理命中态，关闭下拉
  const handleSwitchToModel = useCallback((model: string) => {
    switchToModel(model);
    if (onChange !== undefined) {
      onChange(addModelToTagValue(value, model));
    }
    setOpen(false);
  }, [switchToModel, onChange, value]);

  // 注意：不要把 searchValue 作为受控属性传给 antd Select（避免 tags 模式下重复添加 tag）
  const mergedValueSelectProps = useMemo<SelectProps>(() => ({
    showSearch: true,
    loading: loading || searching,
    filterOption: modelFilterOption,
    onSearch: handleSearch,
    inputValue,
    dropdownRender: createModelDropdownRender(marketingHit, handleSwitchToModel),
    onDropdownVisibleChange: (nextOpen: boolean) => {
      if (nextOpen) setEnabled(true);
      setOpen(nextOpen);
    },
    open,
    ...valueSelectProps,
    // 机型下拉适当放宽（含品牌+市场名称），比输入框窄于自适应宽度
    dropdownStyle: { minWidth: 320 },
  }), [
    loading, searching, handleSearch, inputValue,
    marketingHit, handleSwitchToModel, open, valueSelectProps,
  ]);

  return { options, mergedValueSelectProps, handleSwitchToModel };
}

/**
 * TdsFormNext 机型筛选组件
 *
 * 与 TagFilter 行为一致，额外提供：
 * - 下拉选项展示「型号 (营销名称)」
 * - 搜索时优先匹配原始型号，无结果时降级营销名称搜索并展示切换提示
 * - 点击「点此切换为 {model} 搜索」：自动把 model 写入 value 并关闭下拉
 *
 * 值协议保持 TagFilterValue 不变，filter 提交值仍为原始型号。
 */
export const ModelTagFilter: React.FC<ModelTagFilterProps> = (props) => {
  const {
    fieldKey,
    filterName,
    fieldScope,
    options: externalOptions,
    valueSelectProps,
    value,
    onChange,
    ...restProps
  } = props;

  const { options, mergedValueSelectProps } = useModelInjectedState({
    fieldKey,
    filterName,
    fieldScope: fieldScope as FieldScope | null,
    valueSelectProps,
    value,
    onChange,
  });

  return (
    <TagFilter
      {...restProps}
      key={options.length} // 选项从空→非空时强制重挂载，使 useState 拿到最新选项
      value={value}
      onChange={onChange}
      fieldKey={fieldKey}
      filterName={filterName}
      // 禁用 TagFilter 内部远程加载，选项由 useDeviceModelOptions 统一供给
      fieldScope={null}
      options={externalOptions?.length ? externalOptions : options}
      valueSelectProps={mergedValueSelectProps}
    />
  );
};

/**
 * 字段编辑面板中的机型筛选编辑器（与 ModelTagFilter 配套）
 */
export const ModelTagFilterEditor: React.FC<ModelTagFilterEditorProps> = (props) => {
  const {
    fieldKey,
    filterName,
    fieldScope,
    options: externalOptions,
    valueSelectProps,
    value,
    onChange,
    ...restProps
  } = props;

  const { options, mergedValueSelectProps } = useModelInjectedState({
    fieldKey,
    filterName,
    fieldScope: fieldScope as FieldScope | null,
    valueSelectProps,
    value,
    onChange,
  });

  return (
    <TagFilterEditor
      {...restProps}
      key={options.length}
      value={value}
      onChange={onChange}
      fieldKey={fieldKey}
      filterName={filterName}
      fieldScope={null}
      options={externalOptions?.length ? externalOptions : options}
      valueSelectProps={mergedValueSelectProps}
    />
  );
};
